'use strict';

/**
 * Soracom Authentication Core Module
 * Main coordinator for authentication system with modular architecture
 */
window.SoracomAuth = (() => {

  // Core constants
  const SESSION_DURATION = 3600 * 1000; // 1 hour
  const SESSION_WARNING_TIME = 1 * 60 * 1000; // 1 minute before expiration
  const COUNTDOWN_INTERVAL = 1000; // 1 second

  // Centralized auth service configuration
  const AUTH_CONFIG = {
    BASE_URL: 'https://auth.soracom.io',
    HOST: 'auth.soracom.io',
    ENDPOINTS: {
      LOGIN: '/login',
      TOKEN: '/',
      REFRESH: '/token'
    }
  };

  // Session management variables
  let sessionWarningTimeout = null;
  let sessionExpiryTimeout = null;
  let sessionWarningShown = false;
  let countdownInterval = null;

  // Authentication state
  let authState = null;

  // Coverage types cache
  let coverageTypesCache = null;
  let coverageTypesCacheOperator = null;

  // AuthKeys coverage selection mode flag
  let isAuthKeysCoverageSelectionActive = false;

  /**
   * Load authentication state from storage
   * @returns {Promise<Object|null>} Authentication data or null
   */
  async function loadAuthState() {
    const item = localStorage.getItem('soracom_auth_token');

    if (!item) {
      authState = null;
      return null;
    }

    try {
      const storedData = JSON.parse(item);

      const MAX_STORAGE_AGE = 60 * 60 * 1000; // 1 hour
      if (storedData.timestamp && (Date.now() - storedData.timestamp) > MAX_STORAGE_AGE) {
        localStorage.removeItem('soracom_auth_token');
        authState = null;
        return null;
      }

      const decryptedData = await window.SoracomAuthEncryption?.decryptData(storedData);
      if (!decryptedData) {
        console.error('Failed to decrypt auth data:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
        clearAuthData().catch(e => console.error('Failed to clear auth data:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]'));
        authState = null;
        return null;
      }

      const auth = typeof decryptedData === 'string' ? JSON.parse(decryptedData) : decryptedData;

      if (!window.SoracomAuthEncryption?.validateAuthData(auth)) {
        console.error('Auth data validation failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
        clearAuthData().catch(e => console.error('Failed to clear auth data:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]'));
        authState = null;
        return null;
      }

      // Merge coverage value if present
      const plainCov = window.SoracomAuthCoverage?.getStoredCoverage();
      if (plainCov) auth.coverage = plainCov;
      authState = auth;
      return authState;
    } catch (e) {
      console.error('Error parsing auth data from storage:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
      clearAuthData().catch(err => console.error('Failed to clear auth data:', window.SoracomAuthUtils?.sanitizeForLogging?.(err?.message || err) || '[error details unavailable]'));
      authState = null;
      return null;
    }
  }

  /**
   * Save authentication state to storage
   * @returns {Promise<void>}
   */
  async function saveAuthState() {
    if (authState) {
      const serialized = JSON.stringify(authState);
      const keySeed = authState && authState.operatorId ? authState.operatorId : 'default';
      const encryptedData = await window.SoracomAuthEncryption?.encryptData(serialized, keySeed);
      const encryptedSerialized = JSON.stringify(encryptedData);
      localStorage.setItem('soracom_auth_token', encryptedSerialized);
    } else {
      localStorage.removeItem('soracom_auth_token');
    }
  }

  /**
   * Get current authentication state
   * @returns {Object|null} Authentication data or null
   */
  function getAuthState() {
    return authState;
  }

  /**
   * Set authentication state
   * @param {Object|null} newAuth - New authentication data
   */
  function setAuthState(newAuth) {
    authState = newAuth;
    saveAuthState().catch(e => {
      console.error('Failed to save auth state:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
    });
  }

  /**
   * Set authentication state asynchronously
   * @param {Object|null} newAuth - New authentication data
   * @returns {Promise<void>}
   */
  async function setAuthStateAsync(newAuth) {
    authState = newAuth;
    await saveAuthState();
  }

  /**
   * Load stored authentication data
   * @returns {Promise<Object|null>} Authentication data or null
   */
  async function loadStoredAuth() {
    if (authState !== null) {
      return authState;
    }
    return await loadAuthState();
  }

  /**
   * Store authentication data
   * @param {Object} authData - Authentication data to store
   */
  function storeAuth(authData, { encrypt = true } = {}) {
    // Always refresh timestamp
    authData.timestamp = Date.now();

    // Track session start time for new sessions (for maximum lifetime enforcement)
    if (!authData.sessionStartTime) {
      authData.sessionStartTime = authData.timestamp;
    }

    if (encrypt) {
      // Original full-persistence path
      setAuthState(authData);
    } else {
      // Lightweight path for non-sensitive updates
      authState = authData;
    }

    // Ensure session timers are configured
    setupSessionTimers(authData.timestamp);

    // Dispatch global event so other modules react
    try {
      window.dispatchEvent(new CustomEvent('auth-changed', { detail: authData }));
    } catch (e) {
      console.warn('Could not dispatch auth-changed event:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
    }

    // Restore resource loading if blocked
    const safeTimeout = window.SoracomAuthUtils?.validateTimeoutDuration?.(0) || 0;
    setTimeout(() => {
      if (window.SoracomAuthUI?.resourcesBlocked) {
        window.SoracomAuthUI?.restoreResourceLoading();
      }
    }, safeTimeout);

    // Persist coverage value for quick access
    if (authData.coverage) {
      try {
        window.SoracomAuthCoverage?.setStoredCoverage(authData.coverage);
      } catch (e) {
        console.warn('Could not persist coverage:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
      }
    }

    // After storing auth, update page protection
    if (window.SoracomAuthUI && typeof window.SoracomAuthUI.updatePageProtection === 'function') {
      window.SoracomAuthUI.updatePageProtection(authData);
    }
  }

  /**
   * Clear all authentication data
   * @returns {Promise<void>}
   */
  async function clearAuthData() {
    clearSessionTimers();
    clearCoverageTypesCache();
    await setAuthStateAsync(null);
    window.SoracomAuthCoverage?.clearStoredCoverage();

    if (!window.signingOut) {
      const fieldIds = ['operatorId', 'apiKey', 'apiToken', 'authKeyId', 'authKeySecret'];
      fieldIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) {
          el.value = '';
        }
      });
    }

    try {
      if (typeof swaggerUi !== 'undefined' && swaggerUi && swaggerUi.authActions) {
        swaggerUi.authActions.logout(["api_key"]);
        swaggerUi.authActions.logout(["api_token"]);
      }
    } catch (e) {
      console.warn('SwaggerUI logout in clearAuthData failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
    }

    try {
      // Clear all sessionStorage keys used by auth system
      const authKeys = [
        'auth_redirect',
        'soracom_temp_authkeys',
        'authkeys_just_authenticated'
      ];
      authKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });

      // Clear localStorage keys when clearing session
      const localStorageKeys = [
        'soracom_auth_token',
        window.SoracomAuthCoverage?.COVERAGE_KEY
      ];
      localStorageKeys.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch (e) {
      console.warn('Could not clear sessionStorage:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
    }

    if (!window.signingOut) {
      try {
        window.dispatchEvent(new CustomEvent('auth-changed', { detail: null }));
      } catch (e) {
        console.warn('Could not dispatch auth-changed event:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
      }

      const now = Date.now();
      const lastCheck = window.lastAuthUIUpdate || 0;
      const MIN_CHECK_INTERVAL = 1000;

      if (now - lastCheck < MIN_CHECK_INTERVAL) {
        return;
      }

      window.lastAuthUIUpdate = now;

      if (window.requestIdleCallback) {
        requestIdleCallback(() => checkAuthAndUpdateUI(), { timeout: 500 });
      } else {
        setTimeout(checkAuthAndUpdateUI, 0);
      }
    }
  }

  /**
   * Set up session warning and expiry timers
   * @param {number} sessionStartTime - Session start timestamp
   */
  function setupSessionTimers(sessionStartTime) {
    clearSessionTimers();

    const now = Date.now();
    const sessionAge = now - sessionStartTime;
    const timeUntilWarning = SESSION_DURATION - SESSION_WARNING_TIME - sessionAge;
    const timeUntilExpiry = SESSION_DURATION - sessionAge;

    if (timeUntilWarning > 0) {
      sessionWarningTimeout = setTimeout(() => {
        if (!sessionWarningShown) {
          showSessionWarning();
        }
      }, timeUntilWarning);
    } else if (timeUntilExpiry > 0 && !sessionWarningShown) {
      showSessionWarning();
    }

    if (timeUntilExpiry > 0) {
      sessionExpiryTimeout = setTimeout(() => {
        signOut();
      }, timeUntilExpiry);
    } else {
      signOut();
    }
  }

  /**
   * Clear session timers
   */
  function clearSessionTimers() {
    if (sessionWarningTimeout) {
      clearTimeout(sessionWarningTimeout);
      sessionWarningTimeout = null;
    }
    if (sessionExpiryTimeout) {
      clearTimeout(sessionExpiryTimeout);
      sessionExpiryTimeout = null;
    }
  }

  /**
   * Extract and cache coverage types from JWT token
   * @param {string} token - JWT token
   * @param {string} operatorId - Operator ID for cache validation
   * @returns {Array|null} Coverage types array or null if invalid
   */
  function getCoverageTypesFromToken(token, operatorId) {
    // Check cache first - return cached data if operator hasn't changed
    if (coverageTypesCache && coverageTypesCacheOperator === operatorId) {
      return coverageTypesCache;
    }

    // Parse JWT to extract coverage types
    try {
      const parts = token.split('.');
      if (!parts || parts.length < 3) {
        return null;
      }

      const claims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

      if (!claims || !claims.operator || !Array.isArray(claims.operator.coverageTypes)) {
        window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Invalid token structure: missing operator.coverageTypes');
        return null;
      }

      // Cache the results
      coverageTypesCache = claims.operator.coverageTypes;
      coverageTypesCacheOperator = operatorId;

      return coverageTypesCache;
    } catch (e) {
      console.warn('Error parsing JWT for coverage types:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
      return null;
    }
  }

  /**
   * Clear coverage types cache (call when operator changes)
   */
  function clearCoverageTypesCache() {
    coverageTypesCache = null;
    coverageTypesCacheOperator = null;
  }

  /**
   * Show session warning dialog
   */
  function showSessionWarning() {
    if (sessionWarningShown || window.signingOut) {
      return;
    }

    sessionWarningShown = true;

    const overlay = document.createElement('div');
    overlay.id = 'session-warning-overlay';
    overlay.className = 'session-warning-overlay';

    const lang = window.LANG || {};

    // Use native DOM API for XSS prevention instead of innerHTML
    const dialog = document.createElement('div');
    dialog.className = 'session-warning-dialog';

    const content = document.createElement('div');
    content.className = 'session-warning-content';

    const title = document.createElement('h3');
    title.textContent = lang.SESSION_EXPIRING_TITLE;

    const countdownRow = document.createElement('div');
    countdownRow.className = 'countdown-row';

    const countdownPrefix = document.createElement('span');
    countdownPrefix.className = 'countdown-prefix';
    countdownPrefix.textContent = lang.SESSION_EXPIRING_MESSAGE;

    const countdownBox = document.createElement('span');
    countdownBox.id = 'session-countdown';
    countdownBox.className = 'countdown-box';
    countdownBox.textContent = '01:00';

    countdownRow.appendChild(countdownPrefix);
    countdownRow.appendChild(countdownBox);

    const actions = document.createElement('div');
    actions.className = 'session-actions';

    const extendBtn = document.createElement('button');
    extendBtn.id = 'extend-session';
    extendBtn.className = 'auth-primary-button';
    extendBtn.textContent = lang.SESSION_EXTEND_BUTTON;

    const logoutLink = document.createElement('a');
    logoutLink.href = '#';
    logoutLink.id = 'logout-now';
    logoutLink.className = 'session-logout-link';
    logoutLink.textContent = lang.SESSION_LOGOUT_BUTTON;

    actions.appendChild(extendBtn);
    actions.appendChild(logoutLink);

    content.appendChild(title);
    content.appendChild(countdownRow);
    content.appendChild(actions);

    dialog.appendChild(content);
    overlay.appendChild(dialog);

    overlay.className = 'session-warning-overlay';

    document.body.appendChild(overlay);


    let timeLeft = 60;
    const countdownElement = document.getElementById('session-countdown');

    countdownInterval = setInterval(() => {
      timeLeft--;
      if (countdownElement) {
        const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
        const secs = (timeLeft % 60).toString().padStart(2, '0');
        countdownElement.textContent = `${mins}:${secs}`;
      }

      if (timeLeft <= 0) {
        clearInterval(countdownInterval);
        signOut();
      }
    }, COUNTDOWN_INTERVAL);

    const extendButton = document.getElementById('extend-session');
    const logoutButton = document.getElementById('logout-now');

    if (extendButton) {
      extendButton.addEventListener('click', async () => {
        clearInterval(countdownInterval);

        // Disable button during request
        extendButton.disabled = true;
        extendButton.textContent = 'Extending...';

        try {
          // Limit session extensions based on session age
          const currentAuth = getAuthState();
          if (currentAuth) {
            const sessionStartTime = currentAuth.sessionStartTime || currentAuth.timestamp;
            const sessionAge = Date.now() - sessionStartTime;
            const maxSessionAge = 4 * 60 * 60 * 1000; // 4 hours maximum session age

            // If session is older than max age, require re-authentication
            if (sessionAge > maxSessionAge) {
              overlay.remove();
              sessionWarningShown = false;
              // Show message and redirect to sign in
              if (window.SoracomAuth?.showAuthError) {
                window.SoracomAuth.showAuthError('Session has expired. Please sign in again for security.');
              }
              signOut();
              return;
            }

            // Track extension count for additional security
            const currentExtensionCount = currentAuth.extensionCount || 0;
            const maxExtensions = 3; // Maximum 3 extensions per session

            if (currentExtensionCount >= maxExtensions) {
              overlay.remove();
              sessionWarningShown = false;
              if (window.SoracomAuth?.showAuthError) {
                window.SoracomAuth.showAuthError('Maximum session extensions reached. Please sign in again.');
              }
              signOut();
              return;
            }
          }

          // Call backend to refresh/extend the session
          const authConfig = getAuthConfig();
          const response = await fetch(authConfig.BASE_URL + authConfig.ENDPOINTS.REFRESH, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              tokenTimeoutSeconds: 3600  // Request 1 hour token extension
            })
          });

          if (response.ok) {
            const tokenData = await response.json();

            // Update local auth state with new token data
            if (currentAuth && tokenData.token) {
              // Backend controls session lifetime - trust server-side validation
              currentAuth.token = tokenData.token;
              currentAuth.timestamp = Date.now();
              // Preserve original session start time for tracking
              if (!currentAuth.sessionStartTime) {
                currentAuth.sessionStartTime = currentAuth.timestamp;
              }
              currentAuth.extensionCount = (currentAuth.extensionCount || 0) + 1;
              setAuthState(currentAuth);

              overlay.remove();
              sessionWarningShown = false;
              setupSessionTimers(currentAuth.timestamp);
            } else {
              throw new Error('Invalid token response');
            }
          } else if (response.status === 401 || response.status === 403) {
            // Session invalid - force re-authentication
            signOut();
            return;
          } else {
            throw new Error(`Session extension failed: ${response.status}`);
          }
        } catch (error) {
          console.error('Session extension failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(error?.message || error) || '[error details unavailable]');

          // On failure, force logout for security
          signOut();
          return;
        } finally {
          extendButton.disabled = false;
          extendButton.textContent = 'Stay Logged In';
        }
      });
    }

    if (logoutButton) {
      logoutButton.addEventListener('click', () => {
        clearInterval(countdownInterval);
        overlay.remove();
        signOut();
      });
    }
  }

  /**
   * Show authentication error message to user
   * @param {string} message - Error message to display
   */
  function showAuthError(message) {
    // For AuthKeys errors, show inline in the auth panel
    const authRightSection = document.querySelector('.auth-right-section');
    const authHeaderContainer = document.querySelector('.auth-section-header-container');

    // Remove any existing inline error messages
    const existingInlineError = document.querySelector('.auth-inline-error');
    if (existingInlineError) {
      existingInlineError.remove();
    }

    // Place error immediately after the header container
    if (authRightSection && authHeaderContainer) {
      const errorElement = document.createElement('div');
      errorElement.className = 'auth-inline-error';

      errorElement.textContent = message;

      // Insert the error after the header container
      if (authHeaderContainer.nextSibling) {
        authRightSection.insertBefore(errorElement, authHeaderContainer.nextSibling);
      } else {
        authRightSection.appendChild(errorElement);
      }

      // Auto-hide after 1.5 seconds
      const safeTimeout = window.SoracomAuthUtils?.validateTimeoutDuration?.(1500) || 1500;
      setTimeout(() => {
        if (errorElement && errorElement.parentNode) {
          errorElement.classList.add('fade-out');
          setTimeout(() => {
            if (errorElement && errorElement.parentNode) {
              errorElement.remove();
            }
          }, 300);
        }
      }, safeTimeout);

      return;
    }

    // Fallback: use the original floating notification if we can't find the auth panel
    const existingError = document.getElementById('auth-error-message');
    if (existingError) {
      existingError.remove();
    }

    const errorElement = document.createElement('div');
    errorElement.id = 'auth-error-message';
    errorElement.className = 'auth-error-message auth-error-slide-in';

    errorElement.textContent = message;
    document.body.appendChild(errorElement);

    // Auto-hide after 4 seconds
    const safeTimeout = window.SoracomAuthUtils?.validateTimeoutDuration?.(4000) || 4000;
    setTimeout(() => {
      if (errorElement && errorElement.parentNode) {
        errorElement.className = 'auth-error-message auth-error-slide-out';
        setTimeout(() => {
          if (errorElement && errorElement.parentNode) {
            errorElement.remove();
          }
        }, 300);
      }
    }, safeTimeout);
  }

  /**
   * Check session expiration
   */
  function checkSessionExpiration() {
    const authData = getAuthState();
    if (!authData) {
      return;
    }

    if (authData.expires && Date.now() > authData.expires) {
      window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Session expired, clearing auth data');
      showAuthError('Your session has expired. Please sign in again.');
      clearAuthData().catch(e => console.error('Failed to clear auth data:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]'));
      return;
    }
  }

  /**
   * Main authentication check function - only checks stored auth data, no automatic authentication
  /**
   * Main authentication check function - only checks stored auth data, no automatic authentication
   * @returns {Promise<Object|null>} Authentication data or null
   */
  function checkAuth() {
    if (window.authCheckInProgress && window.authCheckPromise) {
      return window.authCheckPromise;
    }

    window.authCheckInProgress = true;

    const authPromise = (async function () {
      // Only check for stored authentication data, don't attempt automatic session authentication
      const storedAuth = await loadStoredAuth();
      if (storedAuth) {
        return storedAuth;
      }

      return null;
    })();

    window.authCheckPromise = authPromise;

    authPromise.finally(() => {
      window.authCheckInProgress = false;
      window.authCheckPromise = null;
    });

    return authPromise;
  }

  /**
   * Attempt authentication when user explicitly requests it
   * @returns {Promise<Object|null>} Authentication data or null
   */
  async function authenticateUser() {
    // First check for existing session
    const sessionAuth = window.SoracomAuthFlows?.checkSessionAuthentication();

    if (sessionAuth && typeof sessionAuth.then === 'function') {
      const result = await sessionAuth;
      if (result) {
        // Store the auth data from the session
        storeAuth(result);
        return result;
      }
      return null;
    }

    if (sessionAuth && sessionAuth !== 'CHECK_AUTH_SERVICE') {
      // Store the auth data from the session
      storeAuth(sessionAuth);
      return sessionAuth;
    }

    // Check stored auth
    const storedAuth = await loadStoredAuth();
    if (storedAuth) {
      return storedAuth;
    }

    // If no stored auth and session suggests checking service, do that
    if (sessionAuth === 'CHECK_AUTH_SERVICE') {
      const now = Date.now();
      const lastAuthCall = window.lastAuthServiceCall || 0;
      const AUTH_SERVICE_RATE_LIMIT = 5000;
      if (now - lastAuthCall < AUTH_SERVICE_RATE_LIMIT) {
        return null;
      }
      window.lastAuthServiceCall = now;

      return window.SoracomAuthFlows?.fetchAuthToken(AUTH_CONFIG.BASE_URL + AUTH_CONFIG.ENDPOINTS.TOKEN)
        .then(userData => {
          if (userData && userData.apiKey && userData.token) {
            const authData = {
              operatorId: userData.operatorId,
              apiKey: userData.apiKey,
              token: userData.token
            };

            if (!window.SoracomAuthEncryption?.validateAuthData(authData)) {
              window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Auth data from service failed validation.');
              showAuthError('Authentication validation failed. Please try again.');
              return null;
            }

            storeAuth(authData);
            return authData;
          }
          window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Auth service: incomplete credentials received');
          showAuthError('Authentication failed: incomplete credentials received.');
          return null;
        })
        .catch(fetchError => {
          console.error('API ERROR in authenticateUser:', window.SoracomAuthUtils?.sanitizeForLogging?.(fetchError.message) || '[error details unavailable]');

          if (fetchError.name === 'TimeoutError') {
            window.SoracomAuthUtils?.productionSafeLog?.('warn', 'The auth check was aborted due to timeout.');
            showAuthError('Authentication timed out. Please check your connection and try again.');
          } else if (fetchError.name === 'NetworkError') {
            window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Network error during authentication check');
            showAuthError('Network error during authentication. Please check your connection.');
          } else {
            window.SoracomAuthUtils?.productionSafeLog?.('warn', window.SoracomAuthUtils?.sanitizeForLogging?.(fetchError.message || 'Authentication check failed'));
            showAuthError('Authentication failed. Please try again or contact support if the problem persists.');
          }
          return null;
        });
    }

    return null;
  }

  /**
   * Update authentication UI
   * @returns {Promise<void>}
   */
  async function updateAuthenticationUI() {
    if (window.signingOut) {
      return;
    }

    const auth = await loadStoredAuth();
    const authPanel = document.getElementById('sandbox-authentication');

    if (authPanel) {
      if (auth) {
        authPanel.classList.add('ready');

        const operatorIdField = document.getElementById('operatorId');
        const apiKeyField = document.getElementById('apiKey');
        const apiTokenField = document.getElementById('apiToken');
        const authKeyIdInput = document.getElementById('authKeyId');
        const authKeySecretInput = document.getElementById('authKeySecret');

        if (operatorIdField && auth.operatorId) {
          operatorIdField.value = auth.operatorId;
        }
        if (apiKeyField && auth.apiKey) {
          apiKeyField.value = auth.apiKey;
        }
        if (apiTokenField && auth.token) {
          apiTokenField.value = auth.token;
        }
        if (authKeyIdInput && auth.authType === 'authkeys') {
          const isImmediateAfterAuth = sessionStorage.getItem('authkeys_just_authenticated');
          if (!isImmediateAfterAuth) {
            // On page refresh - show masked values with actual prefix and disable
            let maskedAuthKeyId = 'key-••••••••••••••••••••••••••••••';
            authKeyIdInput.value = maskedAuthKeyId;
            authKeyIdInput.setAttribute('placeholder', 'AuthKey ID');
            authKeyIdInput.disabled = true;
          } else {
            // Immediately after auth - keep actual values and disable
            authKeyIdInput.disabled = true;
          }
        }
        if (authKeySecretInput && auth.authType === 'authkeys') {
          const isImmediateAfterAuth = sessionStorage.getItem('authkeys_just_authenticated');
          if (!isImmediateAfterAuth) {
            // On page refresh - show masked values with actual prefix and disable
            let maskedAuthKeySecret = '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••';
            authKeySecretInput.value = maskedAuthKeySecret;
            authKeySecretInput.setAttribute('placeholder', 'AuthKey Secret');
            authKeySecretInput.disabled = true;
          } else {
            // Immediately after auth - keep actual values and disable
            authKeySecretInput.disabled = true;
          }
        }

        const ssoSignInButton = document.getElementById('sso-sign-in');
        const ssoSignOutButton = document.getElementById('sso-sign-out');
        const authKeysButton = document.getElementById('authkeys-authenticate');
        const authenticationLabel = document.getElementById('authentication-label');
        const tokenLabel = document.getElementById('token-label');

        if (auth.authType === 'authkeys') {
          // Show sign-in button, hide sign-out button for AuthKeys
          if (ssoSignInButton) {
            ssoSignInButton.style.display = 'block';
          }
          if (ssoSignOutButton) {
            ssoSignOutButton.style.display = 'none';
          }
          if (authKeysButton) {
            if (!window.signingOut && authKeysButton.textContent !== 'Signing out...') {
              authKeysButton.textContent = 'Sign out';
              authKeysButton.disabled = false;
              authKeysButton.classList.remove('auth-button-loading', 'auth-button-disabled');
              authKeysButton.classList.add('sign-out-btn');
              authKeysButton.classList.add('auth-primary-button');
            }
          }
          if (authenticationLabel) {
            authenticationLabel.textContent = 'AuthKeys';
          }
          if (tokenLabel) {
            tokenLabel.textContent = 'Ready';
          }
        } else {
          // Hide sign-in button, show sign-out button for SSO
          if (ssoSignInButton && !window.signingOut) {
            ssoSignInButton.style.display = 'none';
          }
          if (ssoSignOutButton) {
            ssoSignOutButton.style.display = 'block';
            if (!window.signingOut && ssoSignOutButton.textContent !== 'Signing out...') {
              ssoSignOutButton.style.backgroundColor = '#6c757d';
              ssoSignOutButton.style.border = '1px solid #6c757d';
              ssoSignOutButton.style.color = '#ffffff';
            }
          }
          if (authKeysButton) {
            if (!window.signingOut) {
              authKeysButton.textContent = 'Authenticate';
              authKeysButton.disabled = false;
              authKeysButton.style.removeProperty('background-color');
              authKeysButton.style.removeProperty('border-color');
              authKeysButton.style.removeProperty('color');
              authKeysButton.style.cursor = '';
              authKeysButton.style.removeProperty('border-color');
              authKeysButton.classList.remove('sign-out-btn');
              authKeysButton.classList.add('auth-primary-button');
            }
          }
          if (authenticationLabel) {
            authenticationLabel.textContent = 'Soracom';
          }
          if (tokenLabel) {
            tokenLabel.textContent = 'Ready';
          }
        }

        enableCoverageSelection();

        if (auth.coverage) {
          window.SoracomAuthCoverage?.updateCoverageUI(auth.coverage);
        }
      } else {
        authPanel.classList.remove('ready');

        const operatorIdField = document.getElementById('operatorId');
        const apiKeyField = document.getElementById('apiKey');
        const apiTokenField = document.getElementById('apiToken');
        const authKeyIdInput = document.getElementById('authKeyId');
        const authKeySecretInput = document.getElementById('authKeySecret');

        if (operatorIdField) operatorIdField.value = '';
        if (apiKeyField) apiKeyField.value = '';
        if (apiTokenField) apiTokenField.value = '';
        if (authKeyIdInput) authKeyIdInput.value = '';
        if (authKeySecretInput) authKeySecretInput.value = '';

        const ssoSignInButton = document.getElementById('sso-sign-in');
        const ssoSignOutButton = document.getElementById('sso-sign-out');
        const authKeysButton = document.getElementById('authkeys-authenticate');
        const authenticationLabel = document.getElementById('authentication-label');
        const tokenLabel = document.getElementById('token-label');

        if (ssoSignInButton && !window.signingOut) {
          ssoSignInButton.style.display = 'block';
          ssoSignInButton.style.removeProperty('background-color');
          ssoSignInButton.style.removeProperty('border-color');
          ssoSignInButton.style.removeProperty('color');
          ssoSignInButton.disabled = false;
        }
        if (ssoSignOutButton && !window.signingOut) {
          ssoSignOutButton.style.display = 'none';
        }
        if (authKeysButton) {
          if (!window.signingOut && authKeysButton.textContent !== 'Signing out...') {
            authKeysButton.textContent = 'Authenticate';
            authKeysButton.disabled = false;
            authKeysButton.style.removeProperty('background-color');
            authKeysButton.style.removeProperty('border-color');
            authKeysButton.style.cursor = '';
            authKeysButton.classList.remove('sign-out-btn');
            authKeysButton.classList.add('auth-primary-button');
          }
        }
        if (authenticationLabel) {
          authenticationLabel.textContent = 'None';
        }
        if (tokenLabel) {
          tokenLabel.textContent = 'None';
        }

        // Don't disable coverage selection if we're in SSO redirect flow
        const isSSORediect = sessionStorage.getItem('auth_redirect');
        if (!isSSORediect) {
          disableCoverageSelection();
        }
      }
    }

    if (typeof window.SoracomAuthCoverage?.ensureCoveragePanelOpen === 'function') {
      try {
        window.SoracomAuthCoverage?.ensureCoveragePanelOpen();
      } catch (e) {
        console.warn('ensureCoveragePanelOpen() failed', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
      }
    }
    window.SoracomAuthUtils?.updateCopyButtonsState();
  }

  /**
   * Check authentication and update UI
   * @returns {Promise<Object|null>} Authentication data or null
   */
  function checkAuthAndUpdateUI() {
    if (window.signingOut) {
      return Promise.resolve(null);
    }

    return checkAuth().then(async auth => {
      window.SoracomAuthUI?.updateSignOutVisibility(auth);
      window.SoracomAuthUI?.updatePageProtection(auth);
      await updateAuthenticationUI();
      return auth;
    }).catch(async err => {
      console.error('Error in checkAuth() promise:', window.SoracomAuthUtils?.sanitizeForLogging?.(err.message) || '[error details unavailable]');
      window.SoracomAuthUI?.updateSignOutVisibility(null);
      window.SoracomAuthUI?.updatePageProtection(null);
      await updateAuthenticationUI();
      showAuthError('Authentication check failed. Please refresh the page or try signing in again.');
      return null;
    });
  }

  /**
   * Sign out user
   * @returns {Promise<void>}
   */
  async function signOut() {
    window.signingOut = true;

    const signOutButton = document.getElementById('toolbar-sign-out');
    if (signOutButton) {
      const icon = signOutButton.querySelector('i.fa');
      if (icon && !icon.classList.contains('fa-spinner')) {
        icon.className = 'fa fa-spinner fa-spin';
      }
    }

    try {
      window.authCheckInProgress = false;
      window.authCheckPromise = null;
      window.lastAuthServiceCall = null;
      window.lastSessionExtension = null;
      window.lastAuthKeysCall = null;
      window.lastAuthUIUpdate = null;
      window.authSystemInitializing = false;
    } catch (e) {
      window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Error clearing in-memory state variables:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
    }

    try {
      if (typeof swaggerUi !== 'undefined' && swaggerUi && swaggerUi.authActions) {
        swaggerUi.authActions.logout(["api_key", "api_token"]);
      }
    } catch (e) {
      window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Could not clear Swagger UI state:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
    }

    await clearAuthData();

    setTimeout(() => {
      window.location.reload();
    }, 500);

    return Promise.resolve();
  }

  /**
   * Enable coverage selection
   */
  function enableCoverageSelection() {
    const coverageOptions = window.SoracomAuthUtils?.DOM_CACHE.getCoverageOptions();
    if (coverageOptions) {
      coverageOptions.forEach(option => {
        option.classList.remove('disabled');
        option.classList.add('enabled');
        option.style.removeProperty('cursor');
        option.style.removeProperty('opacity');
      });
    }

    setupCoverageClickHandlers();

    // Don't automatically open coverage panel - let users choose when to open it
    // Coverage panel will only open when users click the coverage menu or when API calls require it
  }

  /**
   * Disable coverage selection
   */
  function disableCoverageSelection() {
    const coverageOptions = window.SoracomAuthUtils?.DOM_CACHE.getCoverageOptions();
    if (coverageOptions) {
      coverageOptions.forEach(option => {
        option.classList.add('disabled');
        option.classList.remove('selected', 'enabled');
        option.style.removeProperty('cursor');
        option.style.removeProperty('opacity');
      });
    }

    const coverageLabel = window.SoracomAuthUtils?.DOM_CACHE.getCoverageLabel();
    const coverageCheckmark = window.SoracomAuthUtils?.DOM_CACHE.getCoverageCheckmark();

    if (coverageLabel) {
      coverageLabel.textContent = 'Not Set';
    }

    if (coverageCheckmark) {
      coverageCheckmark.classList.remove('coverage-selected');
    }

    const coveragePanelEl = document.getElementById('coverage-panel');
    const coverageMenuEl = document.getElementById('coverage-menu');
    if (coveragePanelEl) {
      coveragePanelEl.classList.remove('open');
    }
    if (coverageMenuEl) {
      coverageMenuEl.classList.remove('open');
      coverageMenuEl.classList.remove('active');
    }
  }

  /**
   * Set up coverage click handlers
   */
  function setupCoverageClickHandlers() {
    // Refresh DOM cache to ensure we get current elements
    window.SoracomAuthUtils?.DOM_CACHE.invalidate();

    const coverageOptions = document.querySelectorAll('#coverage-panel .coverage, .coverage[data-coverage-type]');
    if (coverageOptions && coverageOptions.length > 0) {
      coverageOptions.forEach(option => {
        option.removeEventListener('click', handleCoverageClick);
        option.addEventListener('click', handleCoverageClick);
      });
    }
  }

  /**
   * Handle coverage click
   * @param {Event} e - Click event
   */
  function handleCoverageClick(e) {
    // AuthKeys coverage selection mode prevents regular coverage handling
    if (isAuthKeysCoverageSelectionActive) {
      return;
    }

    e.preventDefault();

    const selectedCoverage = e.currentTarget.getAttribute('data-coverage-type');
    if (!selectedCoverage) return;

    if (e.currentTarget.classList.contains('disabled')) return;

    // Update visual state immediately for instant feedback
    const allCoverageOptions = document.querySelectorAll('.coverage[data-coverage-type]');
    allCoverageOptions.forEach(option => {
      option.classList.remove('selected');
    });
    e.currentTarget.classList.add('selected');

    // Process coverage selection immediately (no need to defer for coverage changes)
    processCoverageSelection(selectedCoverage, e.currentTarget);
  }

  /**
   * Process coverage selection (deferred for performance)
   * @param {string} selectedCoverage - Coverage type
   * @param {HTMLElement} target - Target element
   */
  function processCoverageSelection(selectedCoverage, target) {
    // Close panel with UX delay (user made explicit selection)
    setTimeout(() => {
      const coveragePanel = document.getElementById('coverage-panel');
      const coverageMenu = document.getElementById('coverage-menu');
      if (coveragePanel) coveragePanel.classList.remove('open');
      if (coverageMenu) {
        coverageMenu.classList.remove('open');
        coverageMenu.classList.remove('active');
      }
    }, window.SoracomAuthCoverage?.COVERAGE_PANEL_CLOSE_DELAY);

    // Update auth object in memory and persist coverage
    const auth = getAuthState();
    if (auth) {
      auth.coverage = selectedCoverage;
      // Update in-memory state immediately
      authState = auth;

      // Persist to localStorage without heavy operations (deferred)
      setTimeout(() => {
        try {
          setAuthState(auth);
        } catch (e) {
          console.warn('Could not persist coverage to localStorage:', e);
        }
      }, 0);
    }

    // Store coverage directly in localStorage (fast)
    window.SoracomAuthCoverage?.setStoredCoverage(selectedCoverage);

    // Update shared UI components immediately
    window.SoracomAuthCoverage?.updateCoverageUI(selectedCoverage);

    // Dispatch lightweight event for coverage changes
    try {
      window.dispatchEvent(new CustomEvent('coverage-changed', { detail: selectedCoverage }));
    } catch (e) {
      console.warn('Could not dispatch coverage-changed event:', e);
    }
  }

  /**
   * Make authenticated API call
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Fetch options
   * @returns {Promise<Object>} API response
   */
  async function callApi(endpoint, options = {}) {
    const authData = getAuthState();
    if (!authData || !authData.apiKey || !authData.token) {
      throw new Error('No authentication data available');
    }

    const defaultOptions = {
      headers: {
        'X-Soracom-API-Key': authData.apiKey,
        'X-Soracom-Token': authData.token,
        'Content-Type': 'application/json'
      }
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers
      }
    };

    try {
      const response = await fetch(endpoint, mergedOptions);
      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.warn('API call failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(error.message) || '[error details unavailable]');
      throw error;
    }
  }

  /**
   * Initialize authentication system
   * @returns {Promise<void>}
   */
  async function initializeAuth() {
    window.SoracomAuthUI?.preventResourceLoading();
    await loadAuthState();

    window.addEventListener('storage', e => {
      if (e.key === 'soracom_auth_token' || e.key === null) {
        if (!window.signingOut) {
          checkAuthAndUpdateUI();
        }
      }
    });

    window.addEventListener('focus', () => checkSessionExpiration());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkSessionExpiration();
      }
    });

    window.authSystemInitializing = true;

    const storedAuth = await loadStoredAuth();
    window.SoracomAuthUI?.updateSignOutVisibility(storedAuth);

    if (storedAuth) {
      const existingOverlay = document.getElementById('auth-overlay');
      if (existingOverlay && (existingOverlay.style.display !== 'none' && !existingOverlay.classList.contains('hidden'))) {
        window.SoracomAuthUI?.hideAuthOverlay();
      }

      // Update UI for the authenticated state
      await updateAuthenticationUI();
      window.authSystemInitializing = false;
      return;
    }

    checkAuthAndUpdateUI().finally(() => {
      window.authSystemInitializing = false;
    });

    window.addEventListener('focus', () => checkSessionExpiration());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        checkSessionExpiration();
      }
    });

    window.SoracomAuthCoverage?.ensureCoveragePanelOpen();
  }

  /**
   * Set up event delegation for authentication UI
   */
  function setupEventDelegation() {
    // Prevent duplicate event listeners
    if (window.authEventDelegationSetup) {
      return;
    }
    window.authEventDelegationSetup = true;

    // Main click handler for all auth interactions
    document.addEventListener('click', handleAuthClicks);

    // AuthKeys form submit handler - more specific targeting
    document.addEventListener('submit', e => {
      if (e.target.classList.contains('authkeys-form') || e.target.closest('.authkeys-form')) {
        e.preventDefault();
        e.stopPropagation();
        authenticateWithAuthKeys().catch(error => console.error('AuthKeys authentication error:', error));
        return false;
      }
    });

    // Additional form submit prevention for auth forms
    document.addEventListener('submit', e => {
      const form = e.target.closest('form');
      if (form && form.querySelector('#authKeyId, #authKeySecret')) {
        e.preventDefault();
        e.stopPropagation();
        authenticateWithAuthKeys().catch(error => console.error('AuthKeys authentication error:', error));
        return false;
      }
    });

    // Outside-click handling for auth toolbar
    document.addEventListener('click', handleOutsideClicks);
  }

  /**
   * Handle authentication-related clicks
   * @param {Event} e - Click event
   */
  function handleAuthClicks(e) {
    // Toolbar sign-out button
    if (e.target.id === 'toolbar-sign-out' || e.target.closest('#toolbar-sign-out')) {
      e.preventDefault();
      const signOutButton = document.getElementById('toolbar-sign-out');
      if (signOutButton) {
        const icon = signOutButton.querySelector('i.fa');
        if (icon && !icon.classList.contains('fa-spinner')) {
          icon.className = 'fa fa-spinner fa-spin';
        }
      }
      signOut();
      return;
    }

    // API sign-out button
    if (e.target.id === 'api-sign-out' ||
      (e.target.parentElement && e.target.parentElement.id === 'api-sign-out')) {
      e.preventDefault();
      signOut();
      return;
    }

    // SSO sign-out button
    if (e.target.id === 'sso-sign-out' || e.target.closest('#sso-sign-out')) {
      e.preventDefault();
      window.signingOut = true;

      const ssoSignOutButton = document.getElementById('sso-sign-out');
      if (ssoSignOutButton) {
        ssoSignOutButton.textContent = 'Signing out...';
        ssoSignOutButton.disabled = true;
        ssoSignOutButton.style.backgroundColor = '#6c757d';
        ssoSignOutButton.style.borderColor = '#6c757d';
        ssoSignOutButton.style.cursor = 'not-allowed';
        ssoSignOutButton.style.color = '#ffffff';
      }

      signOut();
      return;
    }

    // AuthKeys authenticate/sign-out button
    if (e.target.id === 'authkeys-authenticate' || e.target.closest('#authkeys-authenticate')) {
      e.preventDefault();
      e.stopPropagation();

      const authKeysButton = document.getElementById('authkeys-authenticate');
      const currentAuth = getAuthState();

      // If already authenticated with AuthKeys, sign out
      if (currentAuth && currentAuth.authType === 'authkeys') {
        window.signingOut = true;

        if (authKeysButton) {
          authKeysButton.disabled = true;
          authKeysButton.textContent = 'Signing out...';
          authKeysButton.style.backgroundColor = '#6c757d';
          authKeysButton.style.borderColor = '#6c757d';
          authKeysButton.style.color = '#ffffff';
          authKeysButton.style.cursor = 'not-allowed';
          authKeysButton.classList.add('auth-primary-button');
          authKeysButton.classList.add('sign-out-btn');
        }
        signOut();
      } else {
        // If not authenticated with AuthKeys (including if authenticated with SSO), authenticate with AuthKeys
        authenticateWithAuthKeys().catch(error => console.error('AuthKeys authentication error:', error));
      }
      return;
    }

    // Copy buttons
    if (e.target.closest('.copy-button') || e.target.closest('button.ds-button.--icon-clipboard')) {
      e.preventDefault();
      const button = e.target.closest('.copy-button') || e.target.closest('button.ds-button.--icon-clipboard');
      window.SoracomAuthUtils?.handleCopyButton(button);
      return;
    }

    // Auth sign-in buttons
    if (e.target.id === 'auth-signin' || e.target.closest('#auth-signin') ||
      e.target.id === 'authenticate' || e.target.closest('#authenticate')) {
      e.preventDefault();

      const signInLink = e.target.closest('#auth-signin') || e.target.closest('#authenticate') || e.target;

      if (signInLink) {
        signInLink.classList.add('auth-button-loading');
        signInLink.dataset.originalText = signInLink.textContent;
        signInLink.textContent = 'Authenticating...';
        signInLink.disabled = true;
      }

      // Try to authenticate user (check session/stored auth first, then redirect if needed)
      authenticateUser().then(authData => {
        if (authData) {
          // Successfully authenticated - update all UI components
          window.SoracomAuthUI?.hideAuthOverlay();
          window.SoracomAuthUI?.updatePageProtection(authData);
          window.SoracomAuthUI?.updateSignOutVisibility(authData);
          window.SoracomAuthUtils?.updateCopyButtonsState();

          // Update authentication UI including enabling coverage selection
          updateAuthenticationUI();

          // Reset button state
          if (signInLink) {
            signInLink.classList.remove('auth-button-loading');
            signInLink.disabled = false;
            signInLink.textContent = signInLink.dataset.originalText || 'Sign In';
          }
        } else {
          // No existing session, redirect to auth service
          const currentUrl = window.location.href;
          const redirectUrl = `${AUTH_CONFIG.BASE_URL}${AUTH_CONFIG.ENDPOINTS.LOGIN}?redirect=${encodeURIComponent(currentUrl)}`;

          // Update button text to indicate redirect
          if (signInLink) {
            signInLink.textContent = 'Redirecting...';
          }

          sessionStorage.setItem('auth_redirect', 'true');
          window.location.href = redirectUrl;
        }
      }).catch(err => {
        console.error('Authentication error:', err);

        // Reset button state on error
        if (signInLink) {
          signInLink.classList.remove('auth-button-loading');
          signInLink.disabled = false;
          signInLink.textContent = signInLink.dataset.originalText || 'Sign In';
        }
      });
      return;
    }

    // SSO sign-in button
    const ssoButton = document.getElementById('sso-sign-in');
    if (ssoButton && (e.target.id === 'sso-sign-in' || e.target.closest('#sso-sign-in'))) {
      e.preventDefault();

      const originalText = ssoButton.textContent;
      const originalBackgroundColor = ssoButton.style.backgroundColor;
      const originalBorderColor = ssoButton.style.borderColor;

      ssoButton.textContent = 'Authenticating...';
      ssoButton.disabled = true;
      ssoButton.style.backgroundColor = '#6c757d';
      ssoButton.style.borderColor = '#6c757d';
      ssoButton.style.cursor = 'not-allowed';

      // Check if already authenticated with SSO
      const currentAuth = getAuthState();
      if (currentAuth && currentAuth.authType === 'sso') {
        // Already authenticated with SSO, just restore button state
        ssoButton.textContent = originalText;
        ssoButton.disabled = false;
        ssoButton.style.backgroundColor = originalBackgroundColor;
        ssoButton.style.borderColor = originalBorderColor;
        ssoButton.style.removeProperty('cursor');
        return;
      }

      // For SSO authentication (including switching from AuthKeys), force SSO flow
      // Check session first, then redirect if needed
      const sessionAuth = window.SoracomAuthFlows?.checkSessionAuthentication();

      if (sessionAuth && typeof sessionAuth.then === 'function') {
        sessionAuth.then(result => {
          if (result) {
            // Successfully authenticated with SSO session
            window.SoracomAuthUI?.hideAuthOverlay();
            window.SoracomAuthUI?.updatePageProtection(result);
            window.SoracomAuthUI?.updateSignOutVisibility(result);
            window.SoracomAuthUtils?.updateCopyButtonsState();

            // Reset button state
            ssoButton.textContent = originalText;
            ssoButton.disabled = false;
            ssoButton.style.backgroundColor = originalBackgroundColor;
            ssoButton.style.borderColor = originalBorderColor;
            ssoButton.style.removeProperty('cursor');
          } else {
            // No session, redirect to auth service
            redirectToSSOAuth(ssoButton);
          }
        }).catch(err => {
          console.error('SSO authentication error:', err);
          redirectToSSOAuth(ssoButton);
        });
      } else if (sessionAuth && sessionAuth !== 'CHECK_AUTH_SERVICE') {
        // Got SSO auth data directly
        window.SoracomAuthUI?.hideAuthOverlay();
        window.SoracomAuthUI?.updatePageProtection(sessionAuth);
        window.SoracomAuthUI?.updateSignOutVisibility(sessionAuth);
        window.SoracomAuthUtils?.updateCopyButtonsState();

        // Reset button state
        ssoButton.textContent = originalText;
        ssoButton.disabled = false;
        ssoButton.style.backgroundColor = originalBackgroundColor;
        ssoButton.style.borderColor = originalBorderColor;
        ssoButton.style.removeProperty('cursor');
      } else {
        // No session or need to check auth service, redirect
        redirectToSSOAuth(ssoButton);
      }

      function redirectToSSOAuth(button) {
        button.textContent = 'Redirecting...';

        const currentUrl = window.location.href;
        const redirectUrl = `${AUTH_CONFIG.BASE_URL}${AUTH_CONFIG.ENDPOINTS.LOGIN}?redirect=${encodeURIComponent(currentUrl)}`;

        const hostname = window.location.hostname;
        const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';

        if (isLocalDev) {
          // Only log in development
          window.SoracomAuthUtils?.productionSafeLog?.('info', 'Redirecting to auth service from:', hostname);
        }

        try {
          sessionStorage.setItem('auth_redirect', 'true');
          window.location.href = redirectUrl;
        } catch (e) {
          console.error('Could not set signin flags:', e);
          window.location.href = redirectUrl;
        }
      }
    }
  }

  /**
   * Handle outside clicks for auth toolbar
   * @param {Event} e - Click event
   */
  function handleOutsideClicks(e) {
    const toolbar = document.getElementById('sandbox-authentication');
    if (!toolbar) return;

    const openPanels = document.querySelectorAll('#sandbox-authentication .panel.open');
    if (openPanels.length === 0) return;

    // Only process trusted user-initiated events
    if (!e.isTrusted) {
      return;
    }

    // Ensure the event has valid coordinates (real click events have these)
    if (typeof e.clientX !== 'number' || typeof e.clientY !== 'number' ||
      e.clientX === 0 && e.clientY === 0) {
      return;
    }

    // Don't close if clicking on panels, menus, or coverage options
    if (e.target.closest('#sandbox-authentication .panel') ||
      e.target.closest('#sandbox-authentication .menu') ||
      e.target.closest('.coverage[data-coverage-type]') ||
      e.target.classList.contains('coverage')) {
      return;
    }

    // Ignore clicks on SwaggerUI elements that might trigger synthetic events
    if (e.target.closest('#api') ||
      e.target.closest('#sidebar-content') ||
      e.target.closest('.swagger-ui') ||
      e.target.closest('.opblock')) {
      return;
    }

    const rect = toolbar.getBoundingClientRect();
    if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
      return;
    }

    const activeEl = document.activeElement;
    if (activeEl && activeEl.closest && activeEl.closest('#sandbox-authentication')) {
      return;
    }

    // Don't close panels during SSO authentication flow
    const isSSORediect = sessionStorage.getItem('auth_redirect');

    openPanels.forEach(panel => {
      // Skip closing coverage panel if SSO redirect flow
      if (isSSORediect && panel.id === 'coverage-panel') {
        return;
      }
      panel.classList.remove('open');
    });

    const openMenus = document.querySelectorAll('#sandbox-authentication .menu.open, #sandbox-authentication .menu.active');
    openMenus.forEach(menu => {
      // Skip closing coverage menu if SSO redirect flow
      if (isSSORediect && menu.id === 'coverage-menu') {
        return;
      }
      menu.classList.remove('open');
      menu.classList.remove('active');
    });
  }

  /**
   * Authenticate with AuthKeys
   */
  async function authenticateWithAuthKeys() {

    const idField = document.getElementById('authKeyId');
    const secretField = document.getElementById('authKeySecret');
    const authButton = document.getElementById('authkeys-authenticate');


    if (!idField || !secretField) {
      console.error('AuthKey form fields not found');
      return;
    }

    idField.classList.remove('input-error');
    secretField.classList.remove('input-error');

    let authKeyId = idField.value.trim();
    let authKeySecret = secretField.value.trim();

    // Only try to get stored credentials if fields are disabled (user is already authenticated)
    // Don't use stored credentials when fields are empty but enabled (user needs to enter credentials)
    if (idField.disabled && sessionStorage.getItem('soracom_temp_authkeys')) {
      try {
        const encryptedCreds = sessionStorage.getItem('soracom_temp_authkeys');
        const decryptedData = await window.SoracomAuthEncryption?.decryptData(JSON.parse(encryptedCreds));
        if (decryptedData) {
          const credentialsData = JSON.parse(decryptedData);
          // Use stored credentials if current ones are empty and fields are disabled
          if (!authKeyId) authKeyId = credentialsData.authKeyId;
          if (!authKeySecret) authKeySecret = credentialsData.authKeySecret;
        }
      } catch (e) {
        console.warn('Could not retrieve stored AuthKeys:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
      }
    }

    const resetAuthButtonVisualState = () => {
      if (authButton) {
        authButton.disabled = false;
        authButton.classList.remove('loading');
        authButton.style.removeProperty('background-color');
        authButton.style.removeProperty('border-color');
        authButton.style.cursor = '';
        authButton.textContent = 'Authenticate';
        authButton.blur();
      }
    };

    if (!authKeyId || !authKeySecret) {
      if (!authKeyId) idField.classList.add('input-error');
      if (!authKeySecret) secretField.classList.add('input-error');

      // Show error message for missing credentials
      if (window.SoracomAuth?.showAuthError) {
        window.SoracomAuth.showAuthError('Please enter both AuthKey ID and AuthKey Secret.');
      }

      resetAuthButtonVisualState();

      setTimeout(() => {
        idField.classList.remove('input-error');
        secretField.classList.remove('input-error');
      }, 800);
      return;
    }

    idField.classList.remove('input-error');
    secretField.classList.remove('input-error');

    const authFailedBox = document.getElementById('auth-failed');
    if (authFailedBox) {
      authFailedBox.textContent = '';
      authFailedBox.className = '';
    }

    if (authButton) {
      authButton.disabled = true;
      authButton.textContent = 'Select coverage...';
      authButton.style.backgroundColor = '#6c757d';
      authButton.style.cursor = 'not-allowed';
    }

    await showAuthKeysCoverageSelection(authKeyId, authKeySecret, authButton);
  }

  /**
   * Show coverage selection for AuthKeys
   * @param {string} authKeyId - AuthKey ID
   * @param {string} authKeySecret - AuthKey Secret
   * @param {HTMLElement} authButton - Auth button element
   */
  async function showAuthKeysCoverageSelection(authKeyId, authKeySecret, authButton) {
    // Set flag to prevent regular coverage clicks during AuthKeys auth
    isAuthKeysCoverageSelectionActive = true;

    const coveragePanel = document.getElementById('coverage-panel');
    const coverageMenu = document.getElementById('coverage-menu');

    if (!coveragePanel || !coverageMenu) {
      console.warn('Coverage panel elements not found');
      isAuthKeysCoverageSelectionActive = false;
      window.SoracomAuthKeys?.resetAuthKeysButton(authButton);
      return;
    }

    // Store credentials immediately for the coverage selection process
    try {
      const credentialsData = {
        authKeyId: authKeyId,
        authKeySecret: authKeySecret,
        timestamp: Date.now()
      };

      if (window.SoracomAuthEncryption && typeof window.SoracomAuthEncryption.encryptData === 'function') {
        const encryptedCreds = await window.SoracomAuthEncryption.encryptData(JSON.stringify(credentialsData), Date.now());
        if (encryptedCreds) {
          sessionStorage.setItem('soracom_temp_authkeys', JSON.stringify(encryptedCreds));
        }
      } else {
        console.warn('Could not store temporary AuthKeys: encryption not available');
      }
    } catch (e) {
      console.warn('Could not store temporary AuthKeys:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
    }

    coveragePanel.classList.add('open');
    coverageMenu.classList.add('active');

    const authPanel = document.getElementById('authentication-panel');
    if (authPanel) {
      const authHeight = authPanel.getBoundingClientRect().height;
      coveragePanel.style.minHeight = `${authHeight}px`;
      coveragePanel.style.alignItems = 'center';
    }

    const originalFlexDirection = coveragePanel.style.flexDirection;
    const originalGap = coveragePanel.style.gap;
    const originalJustify = coveragePanel.style.justifyContent;
    coveragePanel.style.flexDirection = 'column';
    coveragePanel.style.gap = '1.5rem';
    coveragePanel.style.justifyContent = 'center';

    const coverageOptions = coveragePanel.querySelectorAll('.coverage');
    coverageOptions.forEach(option => {
      option.style.flex = '0 0 auto';
      option.style.minWidth = '420px';
      option.style.maxWidth = '420px';
      option.style.alignSelf = 'center';
    });

    // Remove any existing authkeys-specific elements first to prevent duplicates
    const existingHeading = coveragePanel.querySelector('h4[data-authkeys-heading="true"]');
    if (existingHeading) {
      existingHeading.remove();
    }
    const existingCancel = document.getElementById('coverage-cancel-authkeys');
    if (existingCancel) {
      existingCancel.remove();
    }

    // Create heading
    let headingText = document.createElement('h4');
    headingText.textContent = 'Select your coverage type';
    headingText.setAttribute('data-authkeys-heading', 'true');
    headingText.style.cssText = 'color:#fff;text-align:center;margin:0 0 1rem 0;font-size:1.1rem;font-weight:600;';
    coveragePanel.insertBefore(headingText, coveragePanel.firstChild);

    // Create cancel link
    let cancelLink = document.createElement('span');
    cancelLink.id = 'coverage-cancel-authkeys';
    cancelLink.textContent = 'Cancel';
    cancelLink.style.cssText = 'margin-top:0.5rem;color:#006cb7;cursor:pointer;font-size:0.9rem;text-align:center;background:none;border:none;display:block;';
    coveragePanel.appendChild(cancelLink);

    const handleCancel = (e) => {
      e.preventDefault();

      const fadeMs = 180;
      coveragePanel.style.transition = `opacity ${fadeMs}ms ease`;
      coveragePanel.style.opacity = '0';

      setTimeout(() => {
        cleanupAuthKeysSelectionStyles();
        window.SoracomAuthKeys?.resetAuthKeysButton(authButton);
      }, fadeMs);
    };

    cancelLink.addEventListener('click', handleCancel);

    coverageOptions.forEach(option => {
      option.classList.remove('disabled');
      option.classList.add('enabled');
      option.style.removeProperty('cursor');
      option.style.removeProperty('opacity');
      option.style.transform = 'scale(1.02)';
      option.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
    });

    const handleAuthKeysCoverageSelection = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const selectedCoverage = e.currentTarget.getAttribute('data-coverage-type');
      if (!selectedCoverage) return;
      if (e.currentTarget.classList.contains('disabled')) return;

      // Close panel with a slight delay for better UX
      setTimeout(() => {
        const coveragePanel = document.getElementById('coverage-panel');
        const coverageMenu = document.getElementById('coverage-menu');
        if (coveragePanel) coveragePanel.classList.remove('open');
        if (coverageMenu) {
          coverageMenu.classList.remove('open');
          coverageMenu.classList.remove('active');
        }
      }, window.SoracomAuthCoverage?.COVERAGE_PANEL_CLOSE_DELAY);

      // For AuthKeys flow, we need to make the actual API call to authenticate
      setTimeout(async () => {
        // Determine the API endpoint based on coverage
        let baseUrl;
        if (selectedCoverage === 'g') {
          baseUrl = 'https://g.api.soracom.io';
        } else if (selectedCoverage === 'jp') {
          baseUrl = 'https://jp.api.soracom.io';
        } else {
          console.error('Unknown coverage type:', selectedCoverage);
          cleanupAuthKeysSelectionStyles();
          window.SoracomAuthKeys?.resetAuthKeysButton(authButton);
          return;
        }

        const endpoint = `${baseUrl}/v1/auth`;

        // Store AuthKeys credentials in memory for coverage switching
        let authKeyId, authKeySecret;

        // Get credentials from form fields or stored session
        const idField = document.getElementById('authKeyId');
        const secretField = document.getElementById('authKeySecret');

        if (idField && secretField && !idField.disabled) {
          // Use form values if available and not disabled
          authKeyId = idField.value.trim();
          authKeySecret = secretField.value.trim();
        } else {
          // Get from stored credentials
          try {
            const encryptedCreds = sessionStorage.getItem('soracom_temp_authkeys');
            if (encryptedCreds && window.SoracomAuthEncryption?.decryptData) {
              const decryptedData = await window.SoracomAuthEncryption.decryptData(JSON.parse(encryptedCreds));
              if (decryptedData) {
                const credentialsData = JSON.parse(decryptedData);
                authKeyId = credentialsData.authKeyId;
                authKeySecret = credentialsData.authKeySecret;
              }
            }
          } catch (e) {
            console.warn('Could not retrieve stored AuthKeys for coverage change:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
          }
        }

        if (!authKeyId || !authKeySecret) {
          console.error('No AuthKeys credentials available for coverage change');
          cleanupAuthKeysSelectionStyles();
          window.SoracomAuthKeys?.resetAuthKeysButton(authButton);
          return;
        }

        try {
          // Store/update credentials in session for future coverage switches
          const credentialsData = {
            authKeyId: authKeyId,
            authKeySecret: authKeySecret,
            timestamp: Date.now()
          };

          if (window.SoracomAuthEncryption && typeof window.SoracomAuthEncryption.encryptData === 'function') {
            const encryptedCreds = await window.SoracomAuthEncryption.encryptData(JSON.stringify(credentialsData), Date.now());
            if (encryptedCreds) {
              sessionStorage.setItem('soracom_temp_authkeys', JSON.stringify(encryptedCreds));
            }
          } else {
            console.warn('Could not store temporary AuthKeys: encryption not available');
          }
        } catch (e) {
          console.warn('Could not store temporary AuthKeys:', window.SoracomAuthUtils?.sanitizeForLogging?.(e.message) || '[error details unavailable]');
        }

        try {
          // Call the API to authenticate with AuthKeys
          const result = await window.SoracomAuthKeys?.tryAuthKeysEndpoint(endpoint, authKeyId, authKeySecret, selectedCoverage);

          if (result && result.success) {
            // Store the authentication data
            window.SoracomAuthKeys?.storeAuthKeysData(result, authButton);
            cleanupAuthKeysSelectionStyles();
          } else {
            console.warn('AuthKeys authentication failed:', result?.error || 'Unknown error');

            const currentAuth = getAuthState();
            if (currentAuth) {
              currentAuth.coverage = null;
              setAuthState(currentAuth);
            }
            window.SoracomAuthCoverage?.clearStoredCoverage();

            // Small delay before cleanup to ensure error message is shown
            setTimeout(() => {
              window.SoracomAuthKeys?.resetAuthKeysButton(authButton);
              cleanupAuthKeysSelectionStyles();
              // Clear coverage UI
              window.SoracomAuthCoverage?.updateCoverageUI(null);
            }, 100);
          }
        } catch (error) {
          console.error('AuthKeys authentication failed:', error);

          const currentAuth = getAuthState();
          if (currentAuth) {
            currentAuth.coverage = null;
            setAuthState(currentAuth);
          }
          window.SoracomAuthCoverage?.clearStoredCoverage();

          // Show error if not already shown
          if (window.SoracomAuth?.showAuthError) {
            window.SoracomAuth.showAuthError('Authentication failed. Please check your credentials and try again.');
          }

          // Small delay before cleanup to ensure error message is shown
          setTimeout(() => {
            window.SoracomAuthKeys?.resetAuthKeysButton(authButton);
            cleanupAuthKeysSelectionStyles();
            // Clear coverage UI
            window.SoracomAuthCoverage?.updateCoverageUI(null);
          }, 100);
        }
      }, window.SoracomAuthCoverage?.COVERAGE_STORE_DELAY);
    };

    coverageOptions.forEach(option => {
      option.addEventListener('click', handleAuthKeysCoverageSelection);
    });
  }

  /**
   * Clean up AuthKeys selection styles
   */
  function cleanupAuthKeysSelectionStyles() {
    // Reset the AuthKeys coverage selection flag
    isAuthKeysCoverageSelectionActive = false;

    const coveragePanel = document.getElementById('coverage-panel');
    if (!coveragePanel) return;

    ['minHeight', 'alignItems', 'flexDirection', 'gap', 'justifyContent', 'transition', 'opacity']
      .forEach(prop => { coveragePanel.style[prop] = ''; });

    const cancelEl = document.getElementById('coverage-cancel-authkeys');
    if (cancelEl) cancelEl.remove();

    // Remove heading with data attribute or text content match
    const headingWithAttr = coveragePanel.querySelector('h4[data-authkeys-heading="true"]');
    if (headingWithAttr) {
      headingWithAttr.remove();
    } else {
      // Fallback: remove by text content
      const headingTexts = coveragePanel.querySelectorAll('h4');
      headingTexts.forEach(heading => {
        if (heading.textContent === 'Select your coverage type') {
          heading.remove();
        }
      });
    }

    const coverageOptions = coveragePanel.querySelectorAll('.coverage');
    coverageOptions.forEach(opt => {
      ['flex', 'maxWidth', 'minWidth', 'width', 'alignSelf', 'cursor', 'opacity', 'transform', 'boxShadow']
        .forEach(prop => { opt.style[prop] = ''; });
    });

    coveragePanel.classList.remove('open');
    const coverageMenuEl = document.getElementById('coverage-menu');
    if (coverageMenuEl) coverageMenuEl.classList.remove('active');

    // Restore regular coverage click handlers after cleanup
    setupCoverageClickHandlers();
  }

  // Initialize on DOM ready
  window.addEventListener('DOMContentLoaded', async () => {
    // Setup UI components that depend on the auth system
    window.SoracomAuthUtils?.setupTooltips();
    window.SoracomAuthUtils?.setupCopyButtonEnablement();
    setupCoverageClickHandlers();

    // Check if we're coming back from an auth redirect
    const isAuthRedirect = sessionStorage.getItem('auth_redirect');

    if (isAuthRedirect) {
      // We're returning from auth service, try to get the session
      try {
        const authData = await authenticateUser();
        if (authData) {
          // Successfully authenticated after redirect
          sessionStorage.removeItem('auth_redirect');
          window.SoracomAuthUI?.updatePageProtection(authData);
          await updateAuthenticationUI();
        } else {
          // Failed to get session after redirect
          console.warn('No session found after auth redirect');
          sessionStorage.removeItem('auth_redirect');
          await checkAuthAndUpdateUI();
        }
      } catch (err) {
        console.error('Error checking session after redirect:', err);
        sessionStorage.removeItem('auth_redirect');
        await checkAuthAndUpdateUI();
      }
    } else {
      // Normal page load - just update UI based on stored auth
      const auth = await loadStoredAuth();
      window.SoracomAuthUI?.updatePageProtection(auth);
      await checkAuthAndUpdateUI();
    }

    // Re-setup coverage handlers after UI update, just in case
    setupCoverageClickHandlers();

    // Initialize mobile-specific behaviors
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        window.SoracomAuthUtils?.initMobileSidebarCollapse();
        window.SoracomAuthUtils?.initClickOutsideSidebar();
      });
    } else {
      window.SoracomAuthUtils?.initMobileSidebarCollapse();
      window.SoracomAuthUtils?.initClickOutsideSidebar();
    }
  });

  // Final check on full window load to catch any edge cases
  window.addEventListener('load', () => {
    const auth = getAuthState();
    window.SoracomAuthUI?.updatePageProtection(auth);
  });

  /**
   * Update sign-out button visibility (delegates to UI module)
   * @param {Object|null} auth - Authentication data
   */
  function updateSignOutVisibility(auth) {
    window.SoracomAuthUI?.updateSignOutVisibility(auth);
  }

  // Public API
  return {
    loadStoredAuth,
    storeAuth,
    getAuthState,
    setAuthState,
    clearAuthData,
    checkAuth,
    checkAuthAndUpdateUI,
    authenticateUser,
    signOut,
    showAuthError,
    enableCoverageSelection,
    callApi,
    initializeAuth,
    setupEventDelegation,
    getCoverageTypesFromToken,
    getAuthConfig: () => AUTH_CONFIG
  };

})();

// Set up event delegation immediately when the script loads
window.SoracomAuth.setupEventDelegation();